import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users,
    ArrowLeft,
    Loader2,
    Calculator as CalcIcon,
    Download,
    Building2,
    Star as StarIcon,
    Share2,
} from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Cell,
} from "recharts";
import CountUp from "react-countup";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import { Switch } from "../../components/ui/switch";
import ShareDialog from "../../components/calc/ShareDialog";

const DEPT_COLORS = ["#39c8c1", "#d6b06b", "#123b52", "#22c55e", "#0c2438", "#94a3b8", "#3b82f6", "#a855f7"];

const initialForm = {
    star_target: 4,
    room_count: 100,
    facility_type: "city",
    occupancy_rate: 0.65,
    shift_count: 3,
    extras: {
        restaurant: { exists: true, capacity: 100 },
        spa: true,
        meeting_room: { exists: true, capacity: 80 },
        pool: true,
    },
};

export default function StaffCalculator() {
    const [form, setForm] = useState(initialForm);
    const [defaults, setDefaults] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shareModal, setShareModal] = useState(false);

    useSeo({
        title: "Personel Planlayıcı",
        description: "Otel için personel ihtiyacı planlayıcı: yıldız, oda, vardiya ve ek alanlara göre departman bazlı analiz, yıllık bordro maliyeti.",
        path: "/araclar/personel-planlayici",
    });

    useEffect(() => {
        api.get("/calculator/defaults").then(({ data }) => setDefaults(data)).catch(() => { });
    }, []);

    const update = (path, value) => {
        setForm((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const segs = path.split(".");
            let o = next;
            for (let i = 0; i < segs.length - 1; i++) o = o[segs[i]];
            o[segs[segs.length - 1]] = value;
            return next;
        });
    };

    const calculate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/calculator/staff", form);
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.post("/calculator/staff/pdf", form, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "otelpro-personel-raporu.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deptChartData = result?.departments?.map((d) => ({ name: d.label, value: d.headcount })) || [];

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="staff-calculator-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/araclar" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tüm araçlar
                    </Link>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <Users className="w-8 h-8 text-gold" />
                        Personel Planlayıcı
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Otel tipi, yıldız, oda sayısı ve ek alanlara göre departman bazlı personel ihtiyacı ve aylık/yıllık bordro maliyeti.
                    </p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
                <Card title="Tesis Bilgileri">
                    <div className="space-y-5">
                        <Field label="Tesis Türü" Icon={Building2}>
                            <div className="grid grid-cols-3 gap-2">
                                {(defaults?.facility_types || []).map((f) => (
                                    <Chip
                                        key={f.value}
                                        active={form.facility_type === f.value}
                                        onClick={() => update("facility_type", f.value)}
                                        data-testid={`staff-facility-${f.value}`}
                                    >
                                        {f.label}
                                    </Chip>
                                ))}
                            </div>
                        </Field>

                        <Field label="Hedef Yıldız" Icon={StarIcon}>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => update("star_target", s)}
                                        data-testid={`staff-star-${s}`}
                                        className={`flex-1 py-3 rounded-md border text-sm font-semibold transition ${form.star_target === s ? "bg-gold text-dark-navy border-gold" : "bg-white text-deep-blue border-slate-300 hover:border-gold/40"}`}
                                    >
                                        {Array.from({ length: s }).map(() => "★").join("")}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        <Field label={`Oda Sayısı: ${form.room_count}`}>
                            <div className="flex items-center gap-3">
                                <input
                                    type="range"
                                    min={20}
                                    max={500}
                                    step={5}
                                    value={form.room_count}
                                    onChange={(e) => update("room_count", parseInt(e.target.value, 10))}
                                    className="flex-1 accent-teal"
                                    data-testid="staff-rooms-slider"
                                />
                                <input
                                    type="number"
                                    min={1}
                                    value={form.room_count}
                                    onChange={(e) => update("room_count", parseInt(e.target.value, 10) || 1)}
                                    className="w-20 px-2 py-2 rounded-md border border-slate-300 text-sm font-data text-center"
                                    data-testid="staff-rooms-input"
                                />
                            </div>
                        </Field>

                        <Field label={`Doluluk Oranı: %${Math.round(form.occupancy_rate * 100)}`}>
                            <input
                                type="range"
                                min={20}
                                max={95}
                                value={Math.round(form.occupancy_rate * 100)}
                                onChange={(e) => update("occupancy_rate", parseInt(e.target.value) / 100)}
                                className="w-full accent-teal"
                                data-testid="staff-occupancy"
                            />
                        </Field>

                        <Field label="Vardiya Sayısı">
                            <div className="grid grid-cols-2 gap-2">
                                {[2, 3].map((s) => (
                                    <Chip
                                        key={s}
                                        active={form.shift_count === s}
                                        onClick={() => update("shift_count", s)}
                                        data-testid={`staff-shift-${s}`}
                                    >
                                        {s} Vardiya
                                    </Chip>
                                ))}
                            </div>
                        </Field>

                        <Field label="Ek Alanlar">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <ExtraToggle
                                    label="Restoran"
                                    active={form.extras.restaurant.exists}
                                    onToggle={(v) => update("extras.restaurant.exists", v)}
                                    capacity={form.extras.restaurant.capacity}
                                    onCapacity={(v) => update("extras.restaurant.capacity", v)}
                                    capacityLabel="Kişi"
                                    testId="staff-restaurant"
                                />
                                <ExtraToggle
                                    label="Toplantı Salonu"
                                    active={form.extras.meeting_room.exists}
                                    onToggle={(v) => update("extras.meeting_room.exists", v)}
                                    capacity={form.extras.meeting_room.capacity}
                                    onCapacity={(v) => update("extras.meeting_room.capacity", v)}
                                    capacityLabel="Kişi"
                                    testId="staff-meeting"
                                />
                                <SimpleToggle
                                    label="Spa / Wellness"
                                    active={form.extras.spa}
                                    onToggle={(v) => update("extras.spa", v)}
                                    testId="staff-spa"
                                />
                                <SimpleToggle
                                    label="Yüzme Havuzu"
                                    active={form.extras.pool}
                                    onToggle={(v) => update("extras.pool", v)}
                                    testId="staff-pool"
                                />
                            </div>
                        </Field>

                        <button
                            onClick={calculate}
                            disabled={loading}
                            className="w-full bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm py-3 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-70 shadow-md"
                            data-testid="staff-calculate-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalcIcon className="w-4 h-4" />}
                            Hesapla
                        </button>
                    </div>
                </Card>

                {result ? (
                    <Card title="Sonuç">
                        <div className="bg-dark-navy text-white rounded-lg p-5">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-teal font-semibold">Toplam Personel</span>
                            <div className="mt-1 font-data text-4xl sm:text-5xl font-bold text-gold">
                                <CountUp end={result.total_staff} duration={1.4} />
                                <span className="text-xl text-white/80 ml-2">kişi</span>
                            </div>
                            <div className="mt-2 text-xs text-white/60">
                                Oda başı: <strong className="text-white">{result.staff_per_room}</strong> · Sezonsal ek: <strong className="text-white">+{result.peak_season_extra}</strong>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <ResultStat label="Aylık Bordro" value={result.monthly_payroll_total} accent="teal" />
                            <ResultStat label="Yıllık Bordro" value={result.annual_payroll_total} accent="gold" />
                        </div>

                        <div className="mt-5">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-deep-blue mb-2">Departman Dağılımı</h4>
                            <div className="h-56" data-testid="staff-dept-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={deptChartData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#64748b" angle={-30} textAnchor="end" height={50} interval={0} />
                                        <YAxis tick={{ fontSize: 10 }} stroke="#64748b" />
                                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                            {deptChartData.map((_, i) => (
                                                <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <button
                            onClick={downloadPdf}
                            className="mt-4 w-full bg-deep-blue hover:bg-dark-navy text-white text-sm font-semibold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="staff-pdf-btn"
                        >
                            <Download className="w-4 h-4" /> PDF Rapor İndir
                        </button>
                        <button
                            onClick={() => setShareModal(true)}
                            className="mt-2 w-full bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="staff-share-btn"
                        >
                            <Share2 className="w-4 h-4" /> Sosyal Medyada Paylaş
                        </button>
                    </Card>
                ) : (
                    <Card title="Sonuç">
                        <div className="text-center py-12 text-deep-blue/60 text-sm">
                            <Users className="w-10 h-10 mx-auto mb-3 text-teal/50" />
                            Personel ihtiyacınızı görmek için soldaki formu doldurun.
                        </div>
                    </Card>
                )}
            </div>

            {result && (
                <div className="max-w-5xl mx-auto px-6 pb-14">
                    <Card title="Departman Detay">
                        <table className="w-full text-sm" data-testid="staff-dept-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-deep-blue/70 border-b border-slate-200">
                                    <th className="py-2">Departman</th>
                                    <th className="py-2 text-right">Personel</th>
                                    <th className="py-2 text-right">Ortalama Maaş</th>
                                    <th className="py-2 text-right">Aylık Maliyet</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.departments.map((d) => (
                                    <tr key={d.key} className="border-b border-slate-100 last:border-0">
                                        <td className="py-2.5 font-semibold text-dark-navy">{d.label}</td>
                                        <td className="py-2.5 text-right font-data">{d.headcount}</td>
                                        <td className="py-2.5 text-right font-data">{d.avg_salary_try.toLocaleString("tr-TR")} ₺</td>
                                        <td className="py-2.5 text-right font-data text-deep-blue">{d.monthly_cost_try.toLocaleString("tr-TR")} ₺</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
            )}

            {shareModal && result && (
                <ShareDialog
                    open={shareModal}
                    onClose={() => setShareModal(false)}
                    tool="staff"
                    summary={{
                        total_staff: result.total_staff,
                        staff_per_room: result.staff_per_room,
                        monthly_payroll: result.monthly_payroll_total,
                        annual_payroll: result.annual_payroll_total,
                        star: form.star_target,
                        room_count: form.room_count,
                    }}
                />
            )}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-6">
            {title && <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-4">{title}</h2>}
            {children}
        </div>
    );
}

function Field({ label, Icon, children }) {
    return (
        <div>
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider inline-flex items-center gap-1.5">
                {Icon && <Icon className="w-3.5 h-3.5 text-teal" />}
                {label}
            </span>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function Chip({ active, onClick, children, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-2.5 rounded-md border text-sm font-semibold transition ${active ? "bg-dark-navy text-white border-dark-navy" : "bg-white text-deep-blue border-slate-300 hover:border-teal/50"}`}
            {...rest}
        >
            {children}
        </button>
    );
}

function ExtraToggle({ label, active, onToggle, capacity, onCapacity, capacityLabel, testId }) {
    return (
        <div className={`rounded-md border p-3 transition ${active ? "border-teal bg-teal/5" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-dark-navy">{label}</span>
                <Switch checked={!!active} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
            </div>
            {active && (
                <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-deep-blue/70 uppercase tracking-wider font-semibold">{capacityLabel}</span>
                    <input
                        type="number"
                        min={0}
                        value={capacity}
                        onChange={(e) => onCapacity(parseInt(e.target.value, 10) || 0)}
                        className="w-20 px-2 py-1 rounded border border-slate-300 text-sm font-data text-center"
                        data-testid={`${testId}-capacity`}
                    />
                </div>
            )}
        </div>
    );
}

function SimpleToggle({ label, active, onToggle, testId }) {
    return (
        <div className={`rounded-md border p-3 transition flex items-center justify-between ${active ? "border-teal bg-teal/5" : "border-slate-200 bg-white"}`}>
            <span className="text-sm font-semibold text-dark-navy">{label}</span>
            <Switch checked={!!active} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
        </div>
    );
}

function ResultStat({ label, value, accent = "deep-blue" }) {
    const color = accent === "gold" ? "text-gold-700" : accent === "teal" ? "text-teal-700" : "text-dark-navy";
    return (
        <div className="rounded-md bg-light-bg p-3">
            <div className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">{label}</div>
            <div className={`mt-1 font-data text-xl font-bold ${color}`}>
                <CountUp end={value} decimals={0} duration={1.2} separator="." /> ₺
            </div>
        </div>
    );
}
